// Feature flags for the frontend. Reversible on purpose: flip a boolean (or
// set the matching REACT_APP_* env var at build time) to restore behavior —
// nothing gated by a flag is ever deleted.

// UI shell theme. "light" = the Salesforce Lightning-style shell
// (components/light/LightAppShell.jsx + the [data-ui-theme="light"] tokens in
// index.css) — the go-live default, chosen by the business for launch.
// "baseplate" = the older dark-header pill nav (components/Header.jsx), kept
// intact as the rollback: set REACT_APP_UI_THEME=baseplate and rebuild.
const RAW_UI_THEME = (process.env.REACT_APP_UI_THEME || "").trim().toLowerCase();
export const UI_THEME = RAW_UI_THEME === "baseplate" ? "baseplate" : "light";
export const IS_LIGHT_THEME = UI_THEME === "light";

// Shell inside the light theme: "modern" (default), "lightning" or "sidebar", see below.
// "modern" = the grouped, collapsible left sidebar with a global top bar (components/modern/*), the
// current default. REACT_APP_UI_SHELL=lightning restores the Lightning-style shell, =sidebar the earlier
// sidebar shell; rebuild after changing it. Pages are the same under every shell.
const RAW_SHELL = (process.env.REACT_APP_UI_SHELL || "").trim().toLowerCase();
export const UI_SHELL = RAW_SHELL === "sidebar" ? "sidebar" : RAW_SHELL === "lightning" ? "lightning" : "modern";
export const IS_LIGHTNING = IS_LIGHT_THEME && UI_SHELL === "lightning";
export const IS_MODERN = IS_LIGHT_THEME && UI_SHELL === "modern";

// Legacy/non-core nav — kept default-off as the umbrella override: set
// REACT_APP_SHOW_LEGACY_MENUS=true (e.g. in frontend/.env) and restart the
// dev server / rebuild to restore everything at once, no code changes
// needed. See docs/GO_LIVE_V1_CHECKLIST.md "UI Cleanup" and
// frontend/src/lib/baseplateNav.js. The per-module flags below are the
// supported way to turn a verified module back on individually instead.
export const SHOW_LEGACY_MENUS = process.env.REACT_APP_SHOW_LEGACY_MENUS === "true";

// Per-module override: REACT_APP_SHOW_<NAME>=true/false wins over
// `defaultOn`; unset falls back to `defaultOn`.
//
// `defaultOn` is GO-LIVE OFF for every module below: the v1 launch scope is
// CRM (Leads/Opportunities/Accounts & Contacts/Quotations), HR (Attendance/
// Payroll), Admin (Users/Brands) only — see docs/GO_LIVE_V1_CHECKLIST.md "UI
// Cleanup". Each module IS a verified, fully working vertical slice
// (frontend + live API + persistence + tenant/RBAC + backend tests — see
// docs/MODULE_READINESS_MATRIX.md for the evidence), so this is a nav
// decision, not a readiness gap — flip any one back to `true` (or set its
// REACT_APP_SHOW_* env var) the moment it's wanted back in v1 scope.
function moduleFlag(name, defaultOn) {
  const raw = process.env[`REACT_APP_SHOW_${name}`];
  if (raw === "true") return true;
  if (raw === "false") return false;
  return defaultOn;
}

// Delivery (Projects, D&W Survey/BOQ, Service & Warranty, Outstanding).
// ON for go-live: projects, site surveys, payments and service are the
// operational core of all three divisions. REACT_APP_SHOW_DELIVERY=false hides it.
export const SHOW_DELIVERY = moduleFlag("DELIVERY", true);

// Inventory (Stock, Stock Ledger, Purchasing). ON for go-live: product
// location, dimensions, MRP and the admin-only landing price are the core of
// the showroom/warehouse workflow. REACT_APP_SHOW_INVENTORY=false hides it.
export const SHOW_INVENTORY = moduleFlag("INVENTORY", true);

// Finance (Money Requests, Wallets & Cashbooks, Profit & Loss, Project P&L,
// Tax Invoices, Payments, Petty Cash history). ON for go-live since the finance build (money requests, wallets, P&L):
// set REACT_APP_SHOW_FINANCE=false to hide the whole Finance menu again.
export const SHOW_FINANCE = moduleFlag("FINANCE", true);

// Reports / Executive Analytics — "advanced analytics", outside v1 scope.
export const SHOW_REPORTS = moduleFlag("REPORTS", false);

// Record Chain — outside v1 scope.
export const SHOW_RECORD_CHAIN = moduleFlag("RECORD_CHAIN", false);

// Incentives — outside v1 scope (also removes the People-tab duplicate
// entry added alongside its original Finance-tab one).
export const SHOW_INCENTIVES = moduleFlag("INCENTIVES", false);

// Budgets (backend-only: backend/api_budget.py/models_budget.py, 14 tests,
// prompt_3_budgets.md) — NO frontend page exists yet. Stays off until
// docs/SCREEN_API_MODEL_MATRIX.md's "smallest implementation" (a read-only
// Budgets.jsx) is built — the flag exists now so nav wiring is ready.
export const SHOW_BUDGETS = moduleFlag("BUDGETS", false);

// Wallets (backend-only: backend/api_wallets.py/models_wallet.py, 10 tests,
// prompt_1_wallets.md) — NO frontend page exists yet, AND its Cashbook/
// PettyCash overlap (docs/WALLETS_DESIGN.md) is unresolved: exposing it
// today would show a second, disagreeing cash-in-hand figure next to
// Cashbook/Petty Cash. Stays off until both the UI is built and that
// overlap is resolved.
export const SHOW_WALLETS = moduleFlag("WALLETS", false);
